/* Who a booking about to walk in is actually going to.
 *
 * The Due now chips used to say only what the book said — a time, a client, a
 * service — and leave "so who takes it?" to the desk. On a quiet morning that
 * is a question with an obvious answer and on a Saturday it is the whole
 * question, because the name in the book is a plan made a fortnight ago and the
 * floor is what it is now. Somebody stands at the desk at ten past; the chip has
 * to name the person who will pick them up.
 *
 * Three answers in order, and the order is the rule:
 *
 *   requested  the client asked for them. That outranks everything, including
 *              being mid-service — a request is a promise the board does not
 *              break, and the honest chip says "Ann" and lets the desk see she
 *              is not free yet rather than quietly offering somebody else.
 *   booked     the book names them and they can take it now. Nothing has gone
 *              wrong, the plan holds, and the chip agrees with the book.
 *   next       the book's name cannot take it — mid-service, gone, or with
 *              their own next client too close — so it falls to the rotation,
 *              exactly as a walk-in would. A booking nobody asked for by name is
 *              work that can move, which is the same judgement the walk-in
 *              estimate makes when it writes a booking off against a colleague.
 *
 * The setting "who takes a booked client" changes the middle tier. Under
 * `book` (the default, and what everything above describes) the booked
 * technician comes before the rotation. Under `turn` the book's name is no
 * longer a claim on the work: only a request outranks the rotation, so a
 * booking nobody asked for by name goes to next up, who may or may not be the
 * technician the book named. The booked tier then survives only as the fallback
 * below, when the rotation has nobody.
 *
 * Then one fallback and one refusal. If the rotation has nobody either, the
 * booked technician goes back on the chip unready — "Ann, when she is free" is a
 * true and useful thing to say, and better than a blank where a name goes. With
 * no name to fall back on the answer is null, and null means the chip says
 * nobody rather than inventing a candidate. `wait`, which is a different
 * question with its own machinery, is Next availability's job.
 *
 * ── WHY THIS IS A RULES FILE ─────────────────────────────────────────────────
 *
 * The precedence is the whole of it, and precedence is what silently goes wrong.
 * Every one of these tiers is reachable on an ordinary Saturday, three of them
 * turn on state the caller has to gather from four different places, and getting
 * the order backwards produces a chip that is *plausible* — a real name, a real
 * technician, simply the wrong one. That is not a bug anybody reports; it is a
 * bug where the desk stops trusting the row. So the order is asserted here over
 * plain objects, with no store, no clock and no DOM.
 *
 * Everything expensive stays with the caller. Whether somebody is free for the
 * booking's window, whether an option on it limits who may do the work, where
 * the rotation has got to — all of that is measured in store.js against the
 * book, and arrives here as three flat lookups. This file imports nothing.
 */

/* The pick, from what the caller already knows.
 *
 *   requestedStaffId  who the client asked for, or null. Already checked
 *                     against the book by the caller: a request that has since
 *                     been moved to somebody else is not a request.
 *   bookedStaffId     who the book names, or null.
 *   eligible          staff id -> may do this work at all. Present on the floor
 *                     and allowed by whatever the booking's options restrict it
 *                     to. NOT "performs the service": the book naming somebody
 *                     for a service they are not set up for is the salon's own
 *                     business, and dropping the name would leave the chip
 *                     answering a question nobody asked.
 *   ready             staff id -> could start it now. Free of a square, in
 *                     the rotation, and with room before their own next client.
 *   nextUp            the rotation's order among those who could take it now,
 *                     already narrowed to who performs the work.
 *   followBook        false when the setting sends booked clients by turn
 *                     order; the booked tier is then skipped while anybody is
 *                     ready. Left unsaid it means true.
 *
 * Returns { staffId, kind, ready } or null.
 */
export function takerFor(pick) {
  var p = pick || {};
  var eligible = p.eligible || {};
  var ready = p.ready || {};
  var nextUp = p.nextUp || [];

  // Asked for by name. Reported whether or not they are free, because "she is
  // with somebody, you are after this one" is what the desk is going to say out
  // loud anyway — and a request handed to a colleague to save two minutes is
  // the complaint that outlives the afternoon.
  var req = p.requestedStaffId;
  if (req && eligible[req]) {
    return { staffId: req, kind: 'requested', ready: !!ready[req] };
  }

  // The book's own name, while it still holds.
  // Under turn order the book's name does not outrank the rotation.
  var booked = p.bookedStaffId;
  if (p.followBook !== false && booked && eligible[booked] && ready[booked]) {
    return { staffId: booked, kind: 'booked', ready: true };
  }

  // It does not hold, so the floor decides, exactly as it would for a walk-in.
  if (nextUp.length) return { staffId: nextUp[0], kind: 'next', ready: true };

  // Nobody else free. The book's name is the only true thing left to say, and
  // it is ready if that person is: under turn order the booked tech can be
  // free while the rotation, measured with their own booking in the way, has
  // nobody. Under the book this tier is only reached by somebody not ready.
  if (booked && eligible[booked]) {
    return { staffId: booked, kind: 'booked', ready: !!ready[booked] };
  }
  return null;
}

/* Whether the book's name outranks the rotation for this booking. The setting
 * decides, except for a request the board cannot name: that is a request, but
 * for nobody `takerFor` can put first, so the booked technician is the only
 * name there is to give (and auto-start, which sees a request and starts the
 * booked tech, agrees). `request` is what the booking's request reads as, or
 * null/undefined for none. */
export function followBookFor(followBook, request) {
  if (request && !request.staffId) return true;
  return followBook !== false;
}
